import { useState } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import { useDebounce, useSetState } from '@domain-group/react-hooks'

import useBrand, { UseBrandPayload, useBrandForm } from '@hooks/useBrand'
import useSelector from '@hooks/useSelector'

import handleCallbackAction from '@functions/handleCallbackAction'

import type { BrandModel } from '@models/types'
import useAgencies from '@hooks/useAgencies'

interface UpdateBrandPayload {
  callbacks: {
    action: UseBrandPayload['callbacks']['updateBrand'],
    afterAction: () => void,
  },
  toastText: string,
}

interface UpdateBrandImageSettingsParams {
  selectedEntityIds: number[],
  updateBrandPayload: UpdateBrandPayload,
}

const updateBrandImageSettings = (params: UpdateBrandImageSettingsParams) => {
  const {
    selectedEntityIds, updateBrandPayload,
  } = params

  const updatedOptions = { brand_image_settings_excluded_agency_ids: selectedEntityIds }

  const updateBrandImageSettingsPayload = {
    callbacks: updateBrandPayload.callbacks,
    entityParams: { options: updatedOptions },
    toastText: updateBrandPayload.toastText,
  }

  handleCallbackAction(updateBrandImageSettingsPayload)
}

type UseBrandImageSettingsModalParams = {
  brand: BrandModel,
}

function useManageBrandImageSettingsPermissionsModal(params: UseBrandImageSettingsModalParams) {
  const { brand: initBrand } = params

  const { brand, callbacks: { updateBrand: updateBrandFn }, updating: updatingBrand } = useBrand(initBrand)

  const formPayload = useBrandForm(brand)
  const { entityState, setEntityState } = formPayload

  const brandExcludedAgencyIds = digObject(entityState, 'options.brand_image_settings_excluded_agency_ids', [])

  const { agencies } = useSelector(reduxState => reduxState.entities)

  const defaultState = { string: '' }

  const [state, setState] = useSetState(defaultState)
  const { string } = state

  const debouncedString = useDebounce(string, 200)

  // search & filter agencies by string
  const agenciesPayload = useAgencies({
    filters: { string: debouncedString, brand_id: brand.id },
    performHttpRequests: !!brand,
  })
  const {
    callbacks: {
      loadMore: loadMoreAgencies,
    },
    canLoadMore,
    filteredAgencies,
    filteredAgenciesCount,
    loading: loadingAgencies,
  } = agenciesPayload

  // load selected agencies when modal opens
  useAgencies({
    performHttpRequests: !!brandExcludedAgencyIds.length,
    requestOptions: { agency_ids: brandExcludedAgencyIds },
  })

  const filteredAgencyIds = filteredAgencies.map(agency => agency.id)

  const [selectedEntityIds, setSelectedEntityIds] = useState<number[]>(
    brand.options?.brand_image_settings_excluded_agency_ids || [],
  )

  return {
    agencies,
    brand,
    brandExcludedAgencyIds,
    callbacks: {
      loadMoreAgencies,
      setEntityState,
      setSelectedEntityIds,
      setState,
      updateBrandImageSettings: (
        updateBrandPayload: UpdateBrandPayload,
      ) => updateBrandImageSettings({
        selectedEntityIds,
        updateBrandPayload,
      }),
      updateBrandFn,
    },
    canLoadMore,
    entityState,
    filteredAgenciesCount,
    filteredAgencyIds,
    loadingAgencies,
    selectedEntityIds,
    string,
    updatingBrand,
  }
}

export default useManageBrandImageSettingsPermissionsModal
